/**
 * Lighting, fog and sky colour driven by the simulation clock:
 * dawn peach → day cream → dusk lilac → night navy, interpolated on continuous
 * hours so the change is a slow wash rather than four discrete states. The
 * directional light rides an east→west arc; after sunset it becomes moonlight
 * from the opposite side.
 */
import * as THREE from 'three';
import type { Clock } from '@village/shared';
import { skyAt } from '../palette';

export class SkyRig {
  readonly hemi: THREE.HemisphereLight;
  readonly sun: THREE.DirectionalLight;
  readonly ambient: THREE.AmbientLight;
  private target = new THREE.Object3D();
  private hourFloat = 8;
  private current = 8;

  constructor(private scene: THREE.Scene, private worldSize: number) {
    this.hemi = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.8);
    this.ambient = new THREE.AmbientLight(0xffffff, 0.5);
    this.sun = new THREE.DirectionalLight(0xffffff, 1.4);
    this.sun.castShadow = true;
    const r = worldSize * 0.9;
    const cam = this.sun.shadow.camera as THREE.OrthographicCamera;
    cam.left = -r;
    cam.right = r;
    cam.top = r;
    cam.bottom = -r;
    cam.near = 1;
    cam.far = worldSize * 4;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0012;
    this.sun.shadow.normalBias = 0.03;

    const c = (worldSize - 1) / 2;
    this.target.position.set(c, 0, c);
    this.sun.target = this.target;

    scene.add(this.hemi, this.ambient, this.sun, this.target);
    // far enough back that fog only softens the horizon, never the village
    scene.fog = new THREE.Fog(0xffffff, worldSize * 1.9, worldSize * 4.4);
    this.set(8, true);
  }

  setClock(clock: Clock): void {
    this.hourFloat = clock.hour + clock.minute / 60;
  }

  /** Called every frame; eases the rendered hour toward the simulated one. */
  update(dt: number): void {
    let diff = this.hourFloat - this.current;
    if (diff > 12) diff -= 24;
    if (diff < -12) diff += 24;
    // catch up quickly after a jump (reconnect, fast clock), glide otherwise
    const rate = Math.abs(diff) > 1.5 ? 6 : 2.5;
    this.current = (this.current + diff * Math.min(1, dt * rate) + 24) % 24;
    this.set(this.current);
  }

  private set(hour: number, immediate = false): void {
    if (immediate) this.current = hour;
    const s = skyAt(hour);
    const bg = new THREE.Color(s.sky);
    this.scene.background = bg;
    if (this.scene.fog) (this.scene.fog as THREE.Fog).color.set(s.fog);

    this.hemi.color.set(s.hemiSky);
    this.hemi.groundColor.set(s.hemiGround);
    this.hemi.intensity = s.hemiIntensity;
    this.ambient.intensity = s.ambient;
    this.sun.color.set(s.sun);
    this.sun.intensity = s.sunIntensity;

    // sun arc: 06:00 rises in the east, 20:00 sets in the west
    const a = (Math.PI * (hour - 6)) / 14;
    const r = this.worldSize * 1.2;
    const c = (this.worldSize - 1) / 2;
    const day = hour > 5.6 && hour < 20.4;
    const x = Math.cos(a) * r * (day ? 1 : -1);
    const y = Math.max(day ? Math.sin(a) * r : r * 0.55, r * 0.22);
    this.sun.position.set(c + x, y, c + r * (day ? 0.42 : -0.3));
  }
}
